<script>
    import Form from '../Form.vue';
    import ModalManager from '../../services/modalManager';
    import axios from 'axios';
    import DialogManager from '../../services/DialogManager';
    import jsonApi from '../../json-api-client';
    import apiUrl from '../../services/apiUrl';
    import HandlesErrorResponses from '../../mixins/HandlesErrorResponses';
    import OnlinePlannerSettings from '../OnlinePlanner/Settings.vue';

    export default {
        extends: Form,

        mixins: [HandlesErrorResponses],

        components: {
            'frm-online-planner-settings': OnlinePlannerSettings,
        },

        props: {
            url: {
                type: String,
                required: true,
            },

            onlinePlannerV2Enabled: {
                type: Boolean,
                default: false,
            },

            twinfieldSuccessUrl: {
                type: String,
                required: true,
            },

            twinfieldErrorUrl: {
                type: String,
                required: true,
            },

            twinfieldConnectUrl: {
                type: String,
                required: true,
            },

            twinfieldReauthenticateUrl: {
                type: String,
                required: true,
            },

            twinfieldDataUrl: {
                type: String,
                required: true,
            },

            twinfieldDestroyUrl: {
                type: String,
                required: true,
            },

        },

        data() {
            return {
                loading: false,
                syncing: false,
                connectAfterFetch: false,
                openedTabs: [],
                registrationIdCopied: false,
                onlinePlannerUrlCopied: false,
                activeTab: null,
                onlinePlannerWizardOpen: false,
            };
        },

        mounted() {
            ModalManager.$on('modalClosed', data => {
                const parentId = data?.['parent_id'];

                if (!parentId || !this.$refs.zorgMailOverview) {
                    return;
                }

                // Change the overview and table url to make sure the data fetched is of the correct external interface
                const url = this.$refs.zorgMailOverview.config.url;

                this.$refs.zorgMailOverview.setUrl(url.replace('-1', parentId));
            });
        },

        computed: {
            onlinePlannerConfiguring() {
                return this.onlinePlannerV2Enabled && this.activeTab === 'onlineplanner' && this.onlinePlannerWizardOpen;
            },

            hasTwinfieldAdministration() {
                return !!this.formData.twinfield_administration_id;
            },

            twinfieldRequiresReauthentication() {
                return !!this.formData.twinfield_administration.requires_reauthentication;
            },

            twinfieldAdministrationId: {
                get() {
                    if (!this.hasTwinfieldAdministration) {
                        return null;
                    }

                    return String(this.formData.twinfield_administration_id);
                },
                set(value) {
                    this.formData.twinfield_administration_id = value;
                },
            },

            twinfieldCanConnect() {
                return this.hasTwinfieldAdministration && !this.formData.twinfield_administration.exists_in_connector;
            },

            twinfieldCanConsent() {
                return this.hasTwinfieldAdministration &&
                    this.formData.twinfield_administration.exists_in_connector &&
                    !this.formData.twinfield_administration.is_authenticated &&
                    this.formData.twinfield_administration.consent_uri;
            },

            twinfieldIsAuthenticated() {
                return this.hasTwinfieldAdministration && this.formData.twinfield_administration.is_authenticated;
            },

            submitButtonEnabled() {
                return !this.$store.state.core.requestPoolPending && !this.formSubmitted && !this.syncing && !this.onlinePlannerConfiguring;
            },
        },

        methods: {
            submit(event) {
                if (this.onlinePlannerConfiguring) {
                    if (event) {
                        event.preventDefault();
                    }

                    return;
                }

                Form.methods.submit.call(this, event);
            },

            handleTabChange(slug) {
                this.activeTab = slug;
                this.openedTabs.push(slug);
            },

            navigateToInstitute(instituteId) {
                window.location.assign(this.url.replace(':id', instituteId));
            },

            async syncTwinfield() {
                this.syncing = true;

                try {
                    await jsonApi.axios.post(apiUrl('/twinfield/sync_exportrelations'));
                } catch (err) {
                    this.handleErrorResponse(err).then();
                }

                this.syncing = false;
            },

            async fetchTwinfieldAdministration(id) {
                this.loading = true;

                const url = this.twinfieldDataUrl.replace('_twinfield_administration_id_', id);

                let data;

                try {
                    data = (await axios.get(url)).data.data;
                } catch (err) {
                    return this.handleErrorResponse(err);
                }

                this.$set(this.formData, 'twinfield_administration', data);
                this.loading = false;

                if (this.connectAfterFetch) {
                    this.connectAfterFetch = false;

                    return this.connectTwinfieldAdministration();
                }
            },

            async reauthenticateTwinfieldAdministration() {
                this.loading = true;

                let data;
                try {
                    data = (await axios.post(this.twinfieldReauthenticateUrl.replace('_twinfield_administration_id_', this.twinfieldAdministrationId), {
                        success_uri: this.twinfieldSuccessUrl.replace('_twinfield_administration_id_', this.twinfieldAdministrationId),
                        error_uri: this.twinfieldErrorUrl.replace('_twinfield_administration_id_', this.twinfieldAdministrationId),
                        institute_id: this.formData.id,
                    })).data.data;
                } catch (err) {
                    return this.handleErrorResponse(err);
                }

                this.$set(this.formData, 'twinfield_administration', data);

                if (this.formData.twinfield_administration.consent_uri) {
                    return this.consentTwinfieldAdministration();
                }

                this.loading = false;
            },

            async connectTwinfieldAdministration() {
                this.loading = true;

                let data;
                try {
                    data = (await axios.post(this.twinfieldConnectUrl.replace('_twinfield_administration_id_', this.twinfieldAdministrationId), {
                        success_uri: this.twinfieldSuccessUrl.replace('_twinfield_administration_id_', this.twinfieldAdministrationId),
                        error_uri: this.twinfieldErrorUrl.replace('_twinfield_administration_id_', this.twinfieldAdministrationId),
                        institute_id: this.formData.id,
                    })).data.data;
                } catch (err) {
                    return this.handleErrorResponse(err);
                }

                this.$set(this.formData, 'twinfield_administration', data);

                if (this.twinfieldCanConsent) {
                    return this.consentTwinfieldAdministration();
                }

                this.loading = false;
            },

            async consentTwinfieldAdministration() {
                this.formSubmitted = true;
                window.location.href = this.formData.twinfield_administration.consent_uri;
            },

            async deleteTwinfieldAdministration() {
                await DialogManager.confirm(this.$t('settings.external-interfaces.twinfield-api.messages.delete_confirm'));

                this.loading = true;

                try {
                    await axios.post(this.twinfieldDestroyUrl.replace('_twinfield_administration_id_', this.twinfieldAdministrationId));
                } catch (err) {
                    return this.handleErrorResponse(err);
                }

                this.loading = false;
                this.twinfieldAdministrationId = null;
            },

            handleNewAdministration(id) {
                this.connectAfterFetch = true;
                this.twinfieldAdministrationId = id;
            },

            async copyRegistrationId(value) {
                await this.copyToClipboard(value, 'registrationIdCopied');
            },

            async copyToClipboard(value, copiedKey) {
                await navigator.clipboard.writeText(value);
                this[copiedKey] = true;
                setTimeout(() => (this[copiedKey] = false), 5 * 1000);
            },
        },

        watch: {
            twinfieldAdministrationId(newVal, oldVal) {
                if (!newVal || String(newVal) === String(oldVal)) {
                    return;
                }

                this.fetchTwinfieldAdministration(newVal);
            },

            openedTabs() {
                const uniqueOpenedTabs = Array.from(new Set(this.openedTabs));
                this.formData.opened_tabs = uniqueOpenedTabs.join(',');
            },
        },
    };
</script>
